import { create } from 'zustand';
import { type BoardId, DEFAULT_BOARD, type LeaderboardEntry, type LeaderboardWindow, type Player } from '../models/types';
import { api } from '../services/api';
import { Logger } from '../services/logger';
import { usePlayer } from './player';

const log = new Logger('Leaderboard');

export interface LeaderboardData {
  me: LeaderboardEntry;
  /** Best first; always contains the signed-in player. */
  entries: LeaderboardEntry[];
  upCursor: string | null;
  downCursor: string | null;
  totalPlayers: number;
  loadingUp: boolean;
  loadingDown: boolean;
  /** Bumped every time the window is reloaded, so the screen can re-centre. */
  generation: number;
}

const fromWindow = (w: LeaderboardWindow, generation: number): LeaderboardData => ({
  me: w.me,
  entries: w.entries,
  upCursor: w.upCursor,
  downCursor: w.downCursor,
  totalPlayers: w.totalPlayers,
  loadingUp: false,
  loadingDown: false,
  generation,
});

export const meIndex = (d: LeaderboardData): number => Math.max(0, d.entries.findIndex((e) => e.isMe));

/** One board's state; boards are independent so switching tabs never loses loaded pages. */
export interface BoardState {
  data: LeaderboardData | null;
  loading: boolean;
  error: string | null;
}

export const emptyBoard: BoardState = { data: null, loading: false, error: null };

interface LeaderboardState {
  /** The tab showing on the Ranks screen; kept in memory only. */
  selected: BoardId;
  /** Boards opened so far, by id; a board is absent until it is first loaded. */
  boards: Record<BoardId, BoardState>;
  select: (board: BoardId) => void;
  load: (board?: BoardId) => Promise<void>;
  refresh: (board?: BoardId) => Promise<void>;
  loadUp: (board?: BoardId) => Promise<void>;
  loadDown: (board?: BoardId) => Promise<void>;
  reset: () => void;
}

/** A board's state, or the empty state when it has not been opened yet. */
export const boardState = (s: Pick<LeaderboardState, 'boards'>, board: BoardId): BoardState => s.boards[board] ?? emptyBoard;

let generation = 0;

export const useLeaderboard = create<LeaderboardState>((set, get) => {
  const patch = (board: BoardId, next: Partial<BoardState>) =>
    set((s) => ({ boards: { ...s.boards, [board]: { ...boardState(s, board), ...next } } }));
  const dataOf = (board: BoardId) => boardState(get(), board).data;

  async function loadMore(board: BoardId, up: boolean) {
    const current = dataOf(board);
    if (!current) return;
    const cursor = up ? current.upCursor : current.downCursor;
    const busy = up ? current.loadingUp : current.loadingDown;
    if (cursor == null || busy) return;
    patch(board, { data: { ...current, ...(up ? { loadingUp: true } : { loadingDown: true }) } });
    const gen = current.generation;
    try {
      const page = await api.getLeaderboardPage(cursor, { up, board });
      const latest = dataOf(board);
      // A refresh landed while this page was in flight; the page belongs to the old window.
      if (!latest || latest.generation !== gen) return;
      const known = new Set(latest.entries.map((e) => e.playerId));
      const fresh = page.entries.filter((e) => !known.has(e.playerId));
      patch(board, {
        data: {
          ...latest,
          entries: up ? [...fresh, ...latest.entries] : [...latest.entries, ...fresh],
          ...(up ? { upCursor: page.cursor, loadingUp: false } : { downCursor: page.cursor, loadingDown: false }),
        },
      });
    } catch (e) {
      log.error(`load ${up ? 'up' : 'down'} failed`, { board, error: e });
      const latest = dataOf(board);
      if (latest) patch(board, { data: { ...latest, ...(up ? { loadingUp: false } : { loadingDown: false }) } });
    }
  }

  return {
    selected: DEFAULT_BOARD,
    boards: {},
    select: (board) => set({ selected: board }),
    load: async (board = DEFAULT_BOARD) => {
      patch(board, { loading: true, error: null });
      try {
        patch(board, { data: fromWindow(await api.getLeaderboard(board), generation), loading: false });
      } catch (e) {
        log.error('load failed', { board, error: e });
        patch(board, { loading: false, error: e instanceof Error ? e.message : String(e) });
      }
    },
    refresh: async (board = DEFAULT_BOARD) => {
      try {
        patch(board, { data: fromWindow(await api.getLeaderboard(board), ++generation), error: null });
      } catch (e) {
        log.error('refresh failed', { board, error: e });
        if (!dataOf(board)) patch(board, { error: e instanceof Error ? e.message : String(e) });
      }
    },
    loadUp: (board = DEFAULT_BOARD) => loadMore(board, true),
    loadDown: (board = DEFAULT_BOARD) => loadMore(board, false),
    reset: () => set({ selected: DEFAULT_BOARD, boards: {} }),
  };
});

// A loaded board's window is reloaded whenever the player's own score on it changes.
const myScore: Record<BoardId, (p: Player) => number> = {
  pickups: (p) => p.profile.stats.pickups,
  drops: (p) => p.profile.stats.drops,
};
let lastScores: Record<BoardId, number> | undefined;
usePlayer.subscribe((s) => {
  const player = s.player;
  const prev = lastScores;
  lastScores = player ? Object.fromEntries(Object.entries(myScore).map(([id, f]) => [id, f(player)])) : undefined;
  if (!prev || !lastScores) return;
  for (const [id, next] of Object.entries(lastScores)) {
    if (prev[id] !== next && boardState(useLeaderboard.getState(), id).data) void useLeaderboard.getState().refresh(id);
  }
});
